// The consent register (Part E) — procedure-/admission-specific informed
// consent. Phase 2 of docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md (§5.12/§6.5).
//
// Append-only on purpose: there is no edit button anywhere in this file.
// "Withdraw" creates a new row; the original stays in the list, grayed out
// via its `superseded` flag — the register prints exactly this way too
// (current text/current row, original kept in the audit annex).
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, NativeSelect, Textarea } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { fetchDoctors } from '@/features/appointments/api';
import { useConsents, useCreateConsent, useWithdrawConsent, useProcedures } from '../hooks';
import type { ConsentType, ConsentGivenBy, ConsentSignatureMode, ConsentStatus } from '../types';
import { Section, Empty, useCan, fmtDateTime } from '../ui';

function useErr() {
  const [err, setErr] = useState<string | null>(null);
  const fail = (e: unknown) => setErr(e instanceof ApiError ? e.message : 'Something went wrong.');
  const node = err ? <div className="mb-2 rounded bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">{err}</div> : null;
  return { fail, node, clear: () => setErr(null) };
}

const CONSENT_TYPES: { value: ConsentType; label: string }[] = [
  { value: 'general_admission', label: 'General admission' },
  { value: 'treatment', label: 'Treatment' },
  { value: 'procedure', label: 'Surgery / procedure' },
  { value: 'anaesthesia', label: 'Anaesthesia / sedation' },
  { value: 'blood', label: 'Blood / products' },
  { value: 'high_risk', label: 'High-risk' },
  { value: 'hiv_test', label: 'HIV test' },
  { value: 'media_research', label: 'Photography / teaching / research' },
  { value: 'data_ai', label: 'Data / AI use' },
  { value: 'lama_dama', label: 'LAMA / DAMA' },
  { value: 'dnr', label: 'DNR / DNAR' },
  { value: 'refusal', label: 'Refusal of treatment' },
];
const GIVEN_BY: ConsentGivenBy[] = ['patient', 'lar', 'guardian'];
const SIGNATURE_MODES: ConsentSignatureMode[] = ['wet', 'thumb', 'esign', 'otp'];
const STATUSES: ConsentStatus[] = ['obtained', 'refused', 'pending'];

const STATUS_BADGE: Record<ConsentStatus, 'success' | 'danger' | 'neutral' | 'warn'> = {
  obtained: 'success', refused: 'danger', withdrawn: 'neutral', pending: 'warn',
};

const EMPTY_FORM = {
  consent_type: 'general_admission' as ConsentType,
  given_by: 'patient' as ConsentGivenBy,
  status: 'obtained' as ConsentStatus,
  signatory_name: '', signatory_relation: '', witness_name: '', witness_relation: '',
  interpreter_name: '', language: '', signature_mode: '' as ConsentSignatureMode | '',
  explained_by_user_id: '', procedure_id: '', procedure_text: '', reason: '',
  risks_alternatives_explained: false,
};

export function ConsentsSection({ admissionId, readOnly }: { admissionId: number; readOnly: boolean }) {
  const can = useCan();
  const mayWrite = can('ip:consent') && !readOnly;
  const { data, isLoading } = useConsents(admissionId);
  const { data: procedures } = useProcedures(admissionId);
  const create = useCreateConsent(admissionId);
  const withdraw = useWithdrawConsent(admissionId);
  const { fail, node, clear } = useErr();
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState(EMPTY_FORM);
  const [doctors, setDoctors] = useState<{ id: number; full_name: string }[]>([]);
  const [withdrawing, setWithdrawing] = useState<number | null>(null);
  const [withdrawReason, setWithdrawReason] = useState('');

  useEffect(() => { fetchDoctors().then(setDoctors).catch(() => {}); }, []);

  const set = (k: keyof typeof EMPTY_FORM, v: string | boolean) => setF((p) => ({ ...p, [k]: v }));
  const reset = () => setF(EMPTY_FORM);
  const needsReason = f.status === 'refused' || ['lama_dama', 'dnr', 'refusal'].includes(f.consent_type);

  return (
    <Section
      focusId="consent"
      title="Consent register"
      subtitle="Append-only — a refusal or withdrawal prints as such, never edited away."
      action={mayWrite && !adding && <Button size="sm" data-focus="consent:new" onClick={() => setAdding(true)}>+ Consent</Button>}
    >
      {adding && (
        <div className="mb-3 grid gap-2 rounded-lg border border-border bg-muted/40 p-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="mb-1 block text-xs font-semibold">Type</label>
            <NativeSelect value={f.consent_type} onChange={(e) => set('consent_type', e.target.value)}>
              {CONSENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </NativeSelect>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold">Given by</label>
            <NativeSelect value={f.given_by} onChange={(e) => set('given_by', e.target.value)}>
              {GIVEN_BY.map((g) => <option key={g} value={g}>{g.toUpperCase()}</option>)}
            </NativeSelect>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold">Status</label>
            <NativeSelect value={f.status} onChange={(e) => set('status', e.target.value)}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </NativeSelect>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold">Signature mode</label>
            <NativeSelect value={f.signature_mode} onChange={(e) => set('signature_mode', e.target.value)}>
              <option value="">— none —</option>
              {SIGNATURE_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
            </NativeSelect>
          </div>

          <div><label className="mb-1 block text-xs font-semibold">Signatory name</label>
            <Input value={f.signatory_name} onChange={(e) => set('signatory_name', e.target.value)} /></div>
          <div><label className="mb-1 block text-xs font-semibold">Signatory relation</label>
            <Input value={f.signatory_relation} onChange={(e) => set('signatory_relation', e.target.value)} placeholder="self / wife / father" /></div>
          <div><label className="mb-1 block text-xs font-semibold">Witness name</label>
            <Input value={f.witness_name} onChange={(e) => set('witness_name', e.target.value)} /></div>
          <div><label className="mb-1 block text-xs font-semibold">Witness relation</label>
            <Input value={f.witness_relation} onChange={(e) => set('witness_relation', e.target.value)} placeholder="staff / relative" /></div>

          <div><label className="mb-1 block text-xs font-semibold">Explained by (doctor)</label>
            <NativeSelect value={f.explained_by_user_id} onChange={(e) => set('explained_by_user_id', e.target.value)}>
              <option value="">— none —</option>
              {doctors.map((d) => <option key={d.id} value={d.id}>{d.full_name}</option>)}
            </NativeSelect></div>
          <div><label className="mb-1 block text-xs font-semibold">Language</label>
            <Input value={f.language} onChange={(e) => set('language', e.target.value)} placeholder="en / ta / hi" /></div>
          <div><label className="mb-1 block text-xs font-semibold">Interpreter</label>
            <Input value={f.interpreter_name} onChange={(e) => set('interpreter_name', e.target.value)} /></div>
          <label className="flex items-center gap-1.5 self-end pb-2 text-[12.5px]">
            <input type="checkbox" checked={f.risks_alternatives_explained} onChange={(e) => set('risks_alternatives_explained', e.target.checked)} />
            Risks / alternatives explained
          </label>

          {(f.consent_type === 'procedure' || f.consent_type === 'anaesthesia') && (
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-semibold">Linked procedure</label>
              <NativeSelect value={f.procedure_id} onChange={(e) => set('procedure_id', e.target.value)}>
                <option value="">— none recorded yet —</option>
                {(procedures ?? []).map((p) => <option key={p.id} value={p.id}>{p.procedure_name}</option>)}
              </NativeSelect>
            </div>
          )}
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-semibold">Procedure (free text, if not yet recorded)</label>
            <Input value={f.procedure_text} onChange={(e) => set('procedure_text', e.target.value)} placeholder="e.g. Lap. cholecystectomy" />
          </div>

          {needsReason && (
            <div className="sm:col-span-2 lg:col-span-4">
              <label className="mb-1 block text-xs font-semibold">Reason</label>
              <Textarea rows={2} value={f.reason} onChange={(e) => set('reason', e.target.value)} />
            </div>
          )}

          <div className="flex justify-end gap-2 sm:col-span-2 lg:col-span-4">
            <Button variant="ghost" size="sm" onClick={() => { setAdding(false); clear(); reset(); }}>Cancel</Button>
            <Button size="sm" loading={create.isPending}
              onClick={() => {
                clear();
                create.mutate({
                  consent_type: f.consent_type,
                  given_by: f.given_by,
                  status: f.status,
                  signatory_name: f.signatory_name || undefined,
                  signatory_relation: f.signatory_relation || undefined,
                  witness_name: f.witness_name || undefined,
                  witness_relation: f.witness_relation || undefined,
                  interpreter_name: f.interpreter_name || undefined,
                  language: f.language || undefined,
                  signature_mode: f.signature_mode || undefined,
                  explained_by_user_id: f.explained_by_user_id ? Number(f.explained_by_user_id) : undefined,
                  procedure_id: f.procedure_id ? Number(f.procedure_id) : undefined,
                  procedure_text: f.procedure_text || undefined,
                  reason: f.reason || undefined,
                  risks_alternatives_explained: f.risks_alternatives_explained,
                } as never, {
                  onSuccess: () => { setAdding(false); reset(); },
                  onError: fail,
                });
              }}>Record consent</Button>
          </div>
        </div>
      )}
      {node}

      {isLoading ? <LoadingState label="Loading consents…" />
        : !data?.length ? <Empty label="No consents recorded" />
        : (
          <div className="space-y-2">
            {data.map((c) => (
              <div key={c.id} data-focus={`consent:${c.id}`} className={`rounded-lg border border-border px-3 py-2 ${c.superseded ? 'opacity-60' : ''}`}>
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[13px] font-semibold">
                      {CONSENT_TYPES.find((t) => t.value === c.consent_type)?.label ?? c.consent_type}
                    </span>
                    {c.procedure_text && <span className="text-[12px] text-ink-muted">— {c.procedure_text}</span>}
                    <Badge variant={STATUS_BADGE[c.status]}>{c.status}</Badge>
                    {c.superseded && <Badge variant="neutral">superseded</Badge>}
                  </div>
                  {mayWrite && !c.superseded && c.status === 'obtained' && (
                    withdrawing === c.id ? (
                      <div className="flex items-center gap-1.5">
                        <Input className="w-40" placeholder="Reason" value={withdrawReason} onChange={(e) => setWithdrawReason(e.target.value)} />
                        <Button size="sm" loading={withdraw.isPending} disabled={!withdrawReason.trim()}
                          onClick={() => {
                            clear();
                            withdraw.mutate({ consentId: c.id, reason: withdrawReason }, {
                              onSuccess: () => { setWithdrawing(null); setWithdrawReason(''); },
                              onError: fail,
                            });
                          }}>Confirm</Button>
                        <Button variant="ghost" size="sm" onClick={() => { setWithdrawing(null); setWithdrawReason(''); }}>Cancel</Button>
                      </div>
                    ) : (
                      <Button variant="ghost" size="sm" onClick={() => setWithdrawing(c.id)}>Withdraw</Button>
                    )
                  )}
                </div>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[12px] text-ink-muted">
                  <span>Given by: {c.given_by}{c.signatory_name ? ` (${c.signatory_name}${c.signatory_relation ? `, ${c.signatory_relation}` : ''})` : ''}</span>
                  {c.explained_by_name && <span>Explained by: Dr {c.explained_by_name}</span>}
                  {c.witness_name && <span>Witness: {c.witness_name}</span>}
                  {c.language && <span>Language: {c.language}</span>}
                </div>
                {c.reason && <div className="mt-1 text-[12.5px] text-ink-muted">Reason: {c.reason}</div>}
                <div className="mt-1 text-[11px] text-ink-muted">
                  {fmtDateTime(c.obtained_at)}
                  {c.supersedes_id ? ` · withdraws consent #${c.supersedes_id}` : ''}
                </div>
              </div>
            ))}
          </div>
        )}
    </Section>
  );
}
